import { CUBICASA_BACKEND_URL, HAS_CUBICASA_BACKEND_URL } from '../config/cubicasa';

export const CUBICASA_MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
export const CUBICASA_LARGE_FILE_WARN_BYTES = 5 * 1024 * 1024;
export const CUBICASA_DEFAULT_MAX_SIDE = 3000;
export const CUBICASA_RETRY_MAX_SIDE = 2000;

const NETWORK_ERROR_MESSAGE =
  'Could not reach the floor plan analysis server. Please try again or upload a smaller JPEG/PNG image.';

const PARSE_LAYOUT_ERROR_MESSAGES = {
  IMAGE_TOO_LARGE:
    'This floor plan image is too large. Please upload a JPEG or PNG under 10 MB.',
  UNSUPPORTED_IMAGE_TYPE: 'Only JPEG and PNG floor plan images are supported.',
  FLOORPLAN_PARSE_FAILED:
    'We could not analyze this floor plan image. Please try a clearer JPEG or PNG image with lower resolution.',
  INTERNAL_SERVER_ERROR: 'Floor plan analysis failed unexpectedly. Please try again.',
  VALIDATION_ERROR: 'The request could not be processed. Please check your upload.',
};

const MASK_DEFINITIONS = [
  { id: 'wall', label: 'Wall mask', keys: ['wallMask', 'wallsMask', 'masks.wall'] },
  { id: 'door', label: 'Door mask', keys: ['doorMask', 'doorsMask', 'masks.door'] },
  { id: 'window', label: 'Window mask', keys: ['windowMask', 'windowsMask', 'masks.window'] },
  { id: 'room', label: 'Room mask', keys: ['roomMask', 'roomsMask', 'masks.room'] },
  { id: 'overlay', label: 'Overlay mask', keys: ['overlayMask', 'masks.overlay'] },
];

const HIDDEN_DEBUG_IMAGE_KEYS = new Set([
  'horizontal_wall_mask',
  'vertical_wall_mask',
  'combined_hv_wall_mask',
  'residual_diagonal_mask',
  'diagonal_lines_overlay',
]);

const ALLOWED_EXTENSIONS = new Set(['jpg', 'jpeg', 'png']);
const ALLOWED_MIME_TYPES = new Set(['image/jpeg', 'image/png']);

/**
 * @param {string} fileName
 * @returns {string | null}
 */
function getFileExtension(fileName) {
  const match = (fileName || '').toLowerCase().match(/\.([^.]+)$/);
  return match ? match[1] : null;
}

/**
 * @param {File} file
 * @returns {boolean}
 */
function isAllowedFloorPlanImageType(file) {
  const mime = (file.type || '').toLowerCase();
  const ext = getFileExtension(file.name);
  if (ext && ALLOWED_EXTENSIONS.has(ext)) return true;
  if (mime && ALLOWED_MIME_TYPES.has(mime)) return true;
  return false;
}

/**
 * @param {File | null | undefined} file
 * @returns {{ ok: true; warn?: string } | { ok: false; error: string }}
 */
export function validateCubiCasaFloorPlanFile(file) {
  if (!file) {
    return { ok: false, error: 'Please upload an image first.' };
  }
  if (!isAllowedFloorPlanImageType(file)) {
    return { ok: false, error: 'Only JPEG and PNG floor plan images are supported.' };
  }
  if (file.size > CUBICASA_MAX_UPLOAD_BYTES) {
    return {
      ok: false,
      error: 'This image is too large. Please upload a JPEG or PNG under 10 MB.',
    };
  }
  if (file.size > CUBICASA_LARGE_FILE_WARN_BYTES) {
    return { ok: true, warn: 'This image is large. Analysis may take longer.' };
  }
  return { ok: true };
}

/**
 * @param {string} key
 * @returns {string}
 */
function formatDebugImageLabel(key) {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

/**
 * @param {unknown} value
 * @returns {string | null}
 */
function resolveImageSrc(value) {
  if (value == null || value === '') return null;

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    if (/^data:image\//i.test(trimmed)) return trimmed;
    if (/^[A-Za-z0-9+/=]+$/.test(trimmed) && trimmed.length > 64) {
      return `data:image/png;base64,${trimmed}`;
    }
    return null;
  }

  if (typeof value === 'object' && value !== null) {
    const obj = /** @type {Record<string, unknown>} */ (value);
    if (typeof obj.dataUrl === 'string') return resolveImageSrc(obj.dataUrl);
    if (typeof obj.url === 'string') return resolveImageSrc(obj.url);
    if (typeof obj.base64 === 'string') {
      const contentType =
        typeof obj.contentType === 'string' && obj.contentType.trim()
          ? obj.contentType.trim()
          : 'image/png';
      return `data:${contentType};base64,${obj.base64.trim()}`;
    }
  }

  return null;
}

/**
 * @param {unknown} root
 * @param {string} dottedKey
 * @returns {unknown}
 */
function getByDottedKey(root, dottedKey) {
  if (!root || typeof root !== 'object') return undefined;
  if (!dottedKey.includes('.')) {
    return /** @type {Record<string, unknown>} */ (root)[dottedKey];
  }
  const parts = dottedKey.split('.');
  let current = /** @type {unknown} */ (root);
  for (const part of parts) {
    if (!current || typeof current !== 'object') return undefined;
    current = /** @type {Record<string, unknown>} */ (current)[part];
  }
  return current;
}

/**
 * @param {number} originalWidth
 * @param {number} originalHeight
 * @param {number} sentWidth
 * @param {number} sentHeight
 * @param {boolean} frontendResized
 * @param {number} maxFrontendSide
 * @returns {{
 *   originalWidth: number;
 *   originalHeight: number;
 *   sentWidth: number;
 *   sentHeight: number;
 *   frontendResized: boolean;
 *   maxFrontendSide: number;
 * }}
 */
function makePreprocessMetadata(
  originalWidth,
  originalHeight,
  sentWidth,
  sentHeight,
  frontendResized,
  maxFrontendSide,
) {
  return {
    originalWidth,
    originalHeight,
    sentWidth,
    sentHeight,
    frontendResized,
    maxFrontendSide,
  };
}

/**
 * @param {Blob} blob
 * @returns {Promise<HTMLImageElement>}
 */
function loadImageElement(blob) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read image dimensions.'));
    };
    img.src = url;
  });
}

/**
 * @param {Blob} blob
 * @returns {Promise<ImageBitmap | HTMLImageElement>}
 */
async function loadDrawableImage(blob) {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(blob);
    } catch {
      // Some browsers cannot decode every image type via createImageBitmap.
    }
  }
  return loadImageElement(blob);
}

/**
 * @param {ImageBitmap | HTMLImageElement} drawable
 * @returns {{ width: number; height: number }}
 */
function getDrawableDimensions(drawable) {
  if ('width' in drawable && drawable.width > 0 && 'height' in drawable && drawable.height > 0) {
    return { width: drawable.width, height: drawable.height };
  }
  return {
    width: /** @type {HTMLImageElement} */ (drawable).naturalWidth,
    height: /** @type {HTMLImageElement} */ (drawable).naturalHeight,
  };
}

/**
 * @param {HTMLCanvasElement} canvas
 * @param {string} mime
 * @returns {Promise<Blob>}
 */
function canvasToBlob(canvas, mime) {
  return new Promise((resolve, reject) => {
    const quality = mime === 'image/jpeg' ? 0.92 : undefined;
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Could not resize image in the browser.'));
        }
      },
      mime,
      quality,
    );
  });
}

/**
 * @param {File} file
 * @param {{ maxSide?: number }} [options]
 * @returns {Promise<{
 *   file: File;
 *   metadata: ReturnType<typeof makePreprocessMetadata>;
 * }>}
 */
export async function prepareCubiCasaUploadImage(file, options = {}) {
  const maxSide = options.maxSide ?? CUBICASA_DEFAULT_MAX_SIDE;
  const drawable = await loadDrawableImage(file);
  try {
    const { width: originalWidth, height: originalHeight } = getDrawableDimensions(drawable);
    if (!originalWidth || !originalHeight) {
      throw new Error('Could not read image dimensions.');
    }

    const longestSide = Math.max(originalWidth, originalHeight);
    if (longestSide <= maxSide) {
      return {
        file,
        metadata: makePreprocessMetadata(
          originalWidth,
          originalHeight,
          originalWidth,
          originalHeight,
          false,
          maxSide,
        ),
      };
    }

    const scale = maxSide / longestSide;
    const sentWidth = Math.max(1, Math.round(originalWidth * scale));
    const sentHeight = Math.max(1, Math.round(originalHeight * scale));
    const canvas = document.createElement('canvas');
    canvas.width = sentWidth;
    canvas.height = sentHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Could not resize image in the browser.');
    }
    ctx.drawImage(drawable, 0, 0, sentWidth, sentHeight);

    const mime = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
    const blob = await canvasToBlob(canvas, mime);
    const extension = mime === 'image/png' ? 'png' : 'jpg';
    const baseName = (file.name || 'floorplan').replace(/\.[^.]+$/, '');
    const resizedFile = new File([blob], `${baseName}-max-${maxSide}.${extension}`, {
      type: mime,
      lastModified: Date.now(),
    });

    return {
      file: resizedFile,
      metadata: makePreprocessMetadata(
        originalWidth,
        originalHeight,
        sentWidth,
        sentHeight,
        true,
        maxSide,
      ),
    };
  } finally {
    if ('close' in drawable && typeof drawable.close === 'function') {
      drawable.close();
    }
  }
}

/**
 * @param {unknown} rawResponse
 * @param {string} [originalPreviewUrl]
 * @returns {{ raw: unknown; panels: Array<{ id: string; label: string; src: string; downloadable: boolean; downloadName?: string }> }}
 */
export function extractMaskPanels(rawResponse, originalPreviewUrl = '') {
  const panels = [];

  if (originalPreviewUrl) {
    panels.push({
      id: 'original',
      label: 'Original',
      src: originalPreviewUrl,
      downloadable: false,
    });
  }

  const debugImages = getByDottedKey(rawResponse, 'debug.debug_images');
  if (debugImages && typeof debugImages === 'object' && !Array.isArray(debugImages)) {
    for (const [key, value] of Object.entries(
      /** @type {Record<string, unknown>} */ (debugImages),
    )) {
      if (HIDDEN_DEBUG_IMAGE_KEYS.has(key)) continue;
      const resolved = resolveImageSrc(value);
      if (!resolved) continue;
      panels.push({
        id: `debug-${key}`,
        label: formatDebugImageLabel(key),
        src: resolved,
        downloadable: true,
        downloadName: `cubicasa-${key}.png`,
      });
    }
  }

  const debugImageUrls = getByDottedKey(rawResponse, 'debug.debug_image_urls');
  if (debugImageUrls && typeof debugImageUrls === 'object' && !Array.isArray(debugImageUrls)) {
    for (const [key, value] of Object.entries(
      /** @type {Record<string, unknown>} */ (debugImageUrls),
    )) {
      if (HIDDEN_DEBUG_IMAGE_KEYS.has(key)) continue;
      const resolved = resolveImageSrc(value);
      if (!resolved) continue;
      if (panels.some((panel) => panel.id === `debug-${key}`)) continue;
      panels.push({
        id: `debug-url-${key}`,
        label: formatDebugImageLabel(key),
        src: resolved,
        downloadable: true,
        downloadName: `cubicasa-${key}.png`,
      });
    }
  }

  for (const def of MASK_DEFINITIONS) {
    let resolved = null;
    for (const key of def.keys) {
      const candidate = getByDottedKey(rawResponse, key);
      resolved = resolveImageSrc(candidate);
      if (resolved) break;
    }
    if (resolved) {
      panels.push({
        id: def.id,
        label: def.label,
        src: resolved,
        downloadable: true,
        downloadName: `cubicasa-${def.id}-mask.png`,
      });
    }
  }

  return { raw: rawResponse, panels };
}

/**
 * @param {unknown} body
 * @returns {string | null}
 */
function extractBackendErrorMessage(body) {
  if (!body || typeof body !== 'object') return null;
  const record = /** @type {Record<string, unknown>} */ (body);
  if (typeof record.message === 'string' && record.message.trim()) {
    return record.message.trim();
  }
  if (typeof record.error === 'string' && record.error.trim()) {
    const code = record.error.trim();
    if (PARSE_LAYOUT_ERROR_MESSAGES[code]) {
      return PARSE_LAYOUT_ERROR_MESSAGES[code];
    }
    return code;
  }
  if (typeof record.detail === 'string' && record.detail.trim()) {
    return record.detail.trim();
  }
  if (Array.isArray(record.detail) && record.detail.length > 0) {
    const joined = record.detail
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object' && typeof item.msg === 'string') {
          const loc = Array.isArray(item.loc) ? item.loc.join('.') : '';
          return loc ? `${loc}: ${item.msg}` : item.msg;
        }
        return null;
      })
      .filter(Boolean)
      .join(' ');
    return joined || null;
  }
  return null;
}

/**
 * @param {number} status
 * @param {string | null} backendMessage
 * @returns {string}
 */
function formatHttpParseLayoutError(status, backendMessage) {
  if (backendMessage) return backendMessage;
  if (status === 502 || status === 504) {
    return 'The analysis server timed out. Try a smaller or clearer image.';
  }
  return `Floor plan analysis failed with status ${status}. Please try a clearer JPEG or PNG image.`;
}

/**
 * @param {File} preparedFile
 * @returns {Promise<
 *   | { kind: 'ok'; body: unknown }
 *   | { kind: 'network' }
 *   | { kind: 'http'; status: number; message: string | null }
 *   | { kind: 'invalid_json' }
 * >}
 */
async function postParseLayout(preparedFile) {
  const formData = new FormData();
  formData.append('file', preparedFile, preparedFile.name || 'floorplan.jpg');
  formData.append('image', preparedFile, preparedFile.name || 'floorplan.jpg');

  let res;
  try {
    res = await fetch(`${CUBICASA_BACKEND_URL}/api/v1/parse-layout`, {
      method: 'POST',
      body: formData,
    });
  } catch {
    return { kind: 'network' };
  }

  let data = null;
  try {
    data = await res.json();
  } catch {
    data = null;
  }

  if (!res.ok) {
    return {
      kind: 'http',
      status: res.status,
      message: extractBackendErrorMessage(data),
    };
  }

  if (data == null) {
    return { kind: 'invalid_json' };
  }

  if (
    typeof data === 'object' &&
    data !== null &&
    /** @type {Record<string, unknown>} */ (data).success === false
  ) {
    return {
      kind: 'http',
      status: res.status,
      message: extractBackendErrorMessage(data),
    };
  }

  return { kind: 'ok', body: data };
}

/**
 * @param {{ kind: 'network' } | { kind: 'http'; status: number }} result
 * @returns {boolean}
 */
function shouldRetryParseLayout(result) {
  if (result.kind === 'network') return true;
  if (result.kind === 'http') {
    if (result.status >= 400 && result.status < 500 && result.status !== 408) {
      return false;
    }
    return result.status >= 500 || result.status === 408;
  }
  return false;
}

/**
 * @param {{ file: File; onRetrying?: () => void }} params
 * @returns {Promise<
 *   | { ok: true; raw: unknown; panels: ReturnType<typeof extractMaskPanels>['panels']; preprocessMetadata: ReturnType<typeof makePreprocessMetadata>; warn?: string }
 *   | { ok: false; error: string; preprocessMetadata?: ReturnType<typeof makePreprocessMetadata> }
 * >}
 */
export async function requestCubiCasaMaskTest({ file, onRetrying }) {
  if (!HAS_CUBICASA_BACKEND_URL) {
    return {
      ok: false,
      error:
        'CubiCasa backend URL is not configured. Set REACT_APP_CUBICASA_BACKEND_URL in ui/.env.local and restart the dev server.',
    };
  }

  const validation = validateCubiCasaFloorPlanFile(file);
  if (!validation.ok) {
    return { ok: false, error: validation.error };
  }

  try {
    let prepared = await prepareCubiCasaUploadImage(file, { maxSide: CUBICASA_DEFAULT_MAX_SIDE });
    let result = await postParseLayout(prepared.file);

    if (shouldRetryParseLayout(result)) {
      onRetrying?.();
      prepared = await prepareCubiCasaUploadImage(file, { maxSide: CUBICASA_RETRY_MAX_SIDE });
      result = await postParseLayout(prepared.file);
    }

    if (result.kind === 'network') {
      return { ok: false, error: NETWORK_ERROR_MESSAGE, preprocessMetadata: prepared.metadata };
    }

    if (result.kind === 'http') {
      return {
        ok: false,
        error: formatHttpParseLayoutError(result.status, result.message),
        preprocessMetadata: prepared.metadata,
      };
    }

    if (result.kind === 'invalid_json') {
      return {
        ok: false,
        error: 'Floor plan analysis returned a response that could not be parsed. Please try again.',
        preprocessMetadata: prepared.metadata,
      };
    }

    const { raw, panels } = extractMaskPanels(result.body);
    return {
      ok: true,
      raw,
      panels,
      preprocessMetadata: prepared.metadata,
      warn: validation.warn,
    };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err ?? '');
    return {
      ok: false,
      error: message || NETWORK_ERROR_MESSAGE,
    };
  }
}

/**
 * @param {string} src
 * @returns {boolean}
 */
export function canDownloadImageSrc(src) {
  if (!src) return false;
  if (src.startsWith('data:')) return true;
  if (src.startsWith('blob:')) return true;
  try {
    const url = new URL(src, window.location.origin);
    return url.origin === window.location.origin;
  } catch {
    return false;
  }
}
